import React, { useState, useEffect } from "react";
import { CheckCircle2, Circle, Loader2, Sparkles } from "lucide-react";
import { fmtD } from "../lib/date";
import { ink } from "../lib/ink";

const LEVEL_COLOR = { won: "#15803d", hot: "#b91c1c", warm: "#b45309", cold: "#4b5b75" };

export default function FollowupAssessment({ leadId, zh, api }) {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/crm/leads/${leadId}/followup-assessment`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    api.get(`/crm/leads/${leadId}/score-history`)
      .then(({ data }) => { if (alive) setHistory(data || []); })
      .catch(() => { if (alive) setHistory([]); });
    return () => { alive = false; };
  }, [leadId, api]);

  if (loading) return <div className="text-center text-slate-300 py-6" data-testid="assessment-loading"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  if (!data) return <p className="text-center text-slate-300 text-sm py-6">{zh ? "暂无评估数据" : "No assessment"}</p>;

  const color = LEVEL_COLOR[data.level] || "#4b5b75";
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="followup-assessment">
      <div className="flex items-center gap-4 px-4 py-3 bg-slate-50 border-b border-slate-100">
        <div className="relative h-16 w-16 shrink-0" data-testid="assessment-score">
          <svg viewBox="0 0 36 36" className="h-16 w-16 -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"
              strokeDasharray={`${data.score}, 100`} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-lg font-bold text-slate-800 leading-none">{data.score}</span>
            <span className="text-[9px] text-slate-400">/100</span>
          </div>
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold" style={ink(color)} data-testid="assessment-level">{data.levelZh}</span>
            <span className="text-[11px] text-slate-400">{zh ? "阶段" : "Stage"}: {data.stageZh}</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-0.5">{zh ? "最近节点活动" : "Last activity"}: {data.lastActivity || (zh ? "无" : "N/A")}</p>
        </div>
      </div>

      <div className="divide-y divide-slate-50">
        {(data.milestones || []).map((m) => (
          <div key={m.key} data-testid={`milestone-${m.key}`} className="flex items-start gap-2.5 px-4 py-2.5">
            {m.done ? <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" /> : <Circle className="h-4 w-4 text-slate-300 mt-0.5 shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-sm font-medium ${m.done ? "text-slate-800" : "text-slate-400"}`}>{zh ? m.label : m.labelEn}</span>
                {m.date && <span className="text-[10px] text-slate-400">{fmtD(m.date)}</span>}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{m.detail}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="px-4 py-3 bg-indigo-50/50 border-t border-indigo-100">
        <p className="text-xs text-slate-700 leading-relaxed flex gap-1.5" data-testid="assessment-summary">
          <Sparkles className="h-3.5 w-3.5 text-indigo-500 shrink-0 mt-0.5" />
          <span>{data.summary}</span>
        </p>
      </div>

      <ScoreTrend history={history} zh={zh} />
    </div>
  );
}

function ScoreTrend({ history, zh }) {
  const pts = (history || []).filter((h) => typeof h.score === "number");
  if (pts.length < 2) {
    return (
      <div className="px-4 py-2 border-t border-slate-100" data-testid="score-trend">
        <p className="text-[11px] text-slate-400">{zh ? "评分趋势：数据积累中（每日快照，明日起可见变化）" : "Trend: collecting daily snapshots"}</p>
      </div>
    );
  }
  const W = 260, H = 44, pad = 4;
  const xs = pts.map((_, i) => pad + (i * (W - 2 * pad)) / (pts.length - 1));
  const ys = pts.map((p) => H - pad - (p.score / 100) * (H - 2 * pad));
  const path = xs.map((x, i) => `${i ? "L" : "M"}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
  const first = pts[0].score, last = pts[pts.length - 1].score;
  const delta = last - first;
  const up = delta > 0, flat = delta === 0;
  const color = up ? "#15803d" : flat ? "#4b5b75" : "#b91c1c";
  const trendZh = up ? "升温" : flat ? "持平" : "降温";
  const trendEn = up ? "warming up" : flat ? "flat" : "cooling down";
  // 降温预警: 近 3 个快照单调不升且累计下滑 ≥ 10 分 (与后端 _detect_cooling 对齐)
  const w3 = pts.slice(-3).map((p) => p.score);
  const cooling = w3.length === 3 && w3[0] >= w3[1] && w3[1] >= w3[2] && (w3[0] - w3[2]) >= 10;
  return (
    <div className="px-4 py-2.5 border-t border-slate-100" data-testid="score-trend">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-medium text-slate-500">{zh ? "评分趋势" : "Score trend"}</span>
        <div className="flex items-center gap-1.5">
          {cooling && (
            <span data-testid="score-cooling-warning" className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-red-700 bg-red-100 border border-red-200 rounded-full px-1.5 py-0.5">
              {zh ? "降温预警" : "Cooling alert"}
            </span>
          )}
          <span className="text-[11px] font-semibold" style={ink(color)} data-testid="score-trend-delta">{zh ? trendZh : trendEn} {delta > 0 ? `+${delta}` : delta}</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 44 }} preserveAspectRatio="none">
        <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {xs.map((x, i) => <circle key={i} cx={x} cy={ys[i]} r="1.8" fill={color} />)}
      </svg>
      <div className="flex justify-between text-[9px] text-slate-300">
        <span>{pts[0].date?.slice(5)}</span>
        <span>{pts[pts.length - 1].date?.slice(5)}</span>
      </div>
    </div>
  );
}
